import { useCallback } from 'react';
import { SECTION_META, stringFromTiptapDoc, tiptapDocFromString } from './editorState';
import type { DxReportPayload, DxReportSection, DxReportSectionId, TiptapDoc } from '../types';

interface PlainTextStepProps {
    payload: DxReportPayload;
    sectionIds: DxReportSectionId[];
    onDocChange: (id: DxReportSectionId, doc: TiptapDoc) => void;
    /** Override the per-section default placeholder. Keyed by section id. */
    placeholders?: Partial<Record<DxReportSectionId, string>>;
    /** Override the static label shown above each textarea. Defaults to `meta.defaultTitle`. */
    labels?: Partial<Record<DxReportSectionId, string>>;
    /** Visible textarea height in rows. Defaults to 6. */
    rows?: number;
}

/**
 * Form-style step body that renders a list of Tiptap sections as flat
 * `<textarea>` inputs. Used for sections we've intentionally kept simple
 * (Goals — patient + doctor) where rich-text + inline images would be
 * overkill. The on-disk schema stays Tiptap so the PDF renderer walks the
 * same shape regardless of how the editor collected the content.
 */
export function PlainTextStep({
    payload,
    sectionIds,
    onDocChange,
    placeholders,
    labels,
    rows = 6
}: PlainTextStepProps) {
    const sectionsForStep = sectionIds
        .map((id) => payload.sections.find((s): s is DxReportSection => s.id === id))
        .filter((s): s is DxReportSection => s !== undefined);

    const handleChange = useCallback(
        (id: DxReportSectionId, value: string) => {
            onDocChange(id, tiptapDocFromString(value));
        },
        [onDocChange]
    );

    return (
        <div className="flex flex-col gap-5">
            {sectionsForStep.map((section) => {
                const meta = SECTION_META[section.id];
                const label = labels?.[section.id] ?? meta.defaultTitle;
                const placeholder = placeholders?.[section.id] ?? meta.placeholder ?? '';
                const value = stringFromTiptapDoc(section.tiptapJson);
                return (
                    <div key={section.id}>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                            {label}
                        </label>
                        <textarea
                            value={value}
                            onChange={(e) => handleChange(section.id, e.target.value)}
                            rows={rows}
                            placeholder={placeholder}
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm leading-relaxed text-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 resize-y"
                        />
                    </div>
                );
            })}
        </div>
    );
}
